import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * Fetches the presigned view URL for the privacy statement PDF.
 * Uses the public GET /api/settings/privacy-statement endpoint — no auth required.
 * Returns { url, loading } — url is null if not uploaded yet.
 */
const CACHE_KEY = "privacy_statement_url_cache";
const CACHE_TTL_MS = 3500 * 1000; // 3500s — safely under S3's 3600s expiry

const getCached = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { url, expiresAt } = JSON.parse(raw);
    if (Date.now() < expiresAt) return url;
    localStorage.removeItem(CACHE_KEY);
  } catch {}
  return null;
};

const setCached = (url) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ url, expiresAt: Date.now() + CACHE_TTL_MS }));
  } catch {}
};

const usePrivacyStatement = () => {
  const [url, setUrl] = useState(() => getCached());
  const [loading, setLoading] = useState(() => getCached() === null);

  useEffect(() => {
    if (getCached()) { setLoading(false); return; }
    let cancelled = false;

    const fetchUrl = (attempt = 1) => {
      axios
        .get(`${API}/settings/privacy-statement`)
        .then((res) => {
          const fresh = res.data?.url ?? null;
          if (cancelled) return;
          setUrl(fresh);
          if (fresh) setCached(fresh);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 3) {
            setTimeout(() => fetchUrl(attempt + 1), attempt * 1500);
          } else {
            setLoading(false); // give up — don't hang forever
          }
        });
    };

    fetchUrl();
    return () => { cancelled = true; };
  }, []);

  return { url, loading };
};

export default usePrivacyStatement;
